/**
 * CalculationServiceFacade - Phase 3 Service Facade
 * Provides a single entry point for all calculation operations (Paved, Railway, etc.).
 * Manages service dependencies internally and simplifies calling code.
 * Implements caching, error handling, and consistent API patterns.
 *
 * Architecture: UI Layer → Service Facade → Business Logic Layer → Service Layer
 * Performance: Centralized caching, simplified service coordination, unified error handling
 */

import { logDebug, logError, roundNumbers, LogTag } from '../utils/utils';
import { PavedCalculationOrchestrator } from './PavedCalculationOrchestrator';
import { RailwayCalculationOrchestrator, IRailwayOutputData } from './RailwayCalculationOrchestrator';
import { ILayerAnalysisInputs, ILayerAnalysisResult } from '../types/CalculationTypes';
import { OptimizedExcelCellCalculatorService } from './OptimizedExcelCellCalculatorService';
import { IPavedData, IRailwayData } from '../api/IApiResponse';
import { materialDatabaseService } from './MaterialDatabaseService';
import { GEOGRID_CONFIG } from '../config/GeogridConfig';
import { ReliabilityService } from './ReliabilityService';
import { UNIT_CONVERSION } from '../constants/AASHTOConstants';

/**
 * Cache key for calculation results
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface CalculationCacheKey {
    formDataHash: string;
    timestamp: number;
}

/**
 * Complete calculation result with all derived data
 */
// Type definitions for optimized calculation results
interface OptimizedCalculationResults {
    step0_initialSetup: {
        aashto: { mrSubgradeKpa: number; mrSubgradePsi: number };
        materials: {
            surface: { a1: number };
            base: { a2: number };
            subbase: { a3: number };
        };
        drainage: {
            base: { m2: number; m2_stabilised?: number };
            subbase: { m3: number; m3_stabilised?: number };
        };
        lcr: { base: number; subbase: number };
    };
    step1_validatedInputs: { deltaPSI: number; reliability?: number };
    step2_designTraffic: { w18: number };
    step3_requiredSN: {
        snRequired: number;
        aashtoFactors: { f1: number; f2: number; f3: number };
    };
    step4_layerDesign: {
        thickness: {
            unstabilised: { surface: number; base: number; subbase: number };
            stabilised: { surface: number; base: number; subbase: number };
        };
        structuralNumbers?: { unstabilised: number; stabilised: number };
    };
    step5_finalLogW18: {
        unstabilised: number;
        stabilised: number;
        esal: { unstabilised: number; stabilised: number };
    };
}

interface TransformedCalculationResults {
    basicParameters: {
        dpsi: number;
        zr: number;
        structuralNumberSN: number;
        structuralNumberMeters: number;
        mrSubgradeKpa: number;
        mrSubgradePsi: number;
    };
    layerCoefficients: {
        surface: { a1: number; drainageCoefficient: number; thickness: number };
        base: { a2: number; m2: number; m2_stabilised?: number; thickness: number; lcr?: number };
        subbase: { a3: number; m3: number; m3_stabilised?: number; thickness: number; lcr?: number };
    };
    softwareDesign: {
        surface: { unstabilised: number; stabilised: number };
        base: { unstabilised: number; stabilised: number; geogridMaterialName?: string; geogridPosition?: number };
        subbase: { unstabilised: number; stabilised: number; geogridMaterialName?: string; geogridPosition?: number };
        totalSN: { unstabilised: number; stabilised: number };
    };
    esalFactors: {
        w18: number;
        logW18Unstabilised: number;
        logW18Stabilised: number;
        esalUnstabilised: number;
        esalStabilised: number;
        tbr: number;
        f1: number;
        f2: number;
        f3: number;
        tgf: number;
        vtUnstabilised: number;
        vtStabilised: number;
        niUnstabilised: number;
        niStabilised: number;
    };
    snCalculations: {
        snUnstabilised: number;
        snStabilised: number;
        sn1Prime: number;
        sn2Prime: number;
        sn3Prime: number;
    };
    designThickness: {
        unstabilised: { surface: number; base: number; subbase: number };
        stabilised: { surface: number; base: number; subbase: number };
    };
    _originalOptimizedResults: OptimizedCalculationResults;
}

export interface ICompleteCalculationResult {
    // Core AASHTO analysis
    totalSN: number;
    layerAnalysis: ILayerAnalysisResult;

    // Excel compatibility calculations
    detailedCalculations: TransformedCalculationResults;

    // Performance metadata
    cacheHit: boolean;
    calculationTimeMs: number;
}

/**
 * Service Facade for Paved Calculations
 *
 * Benefits:
 * - Single entry point for all calculations
 * - Internal service dependency management
 * - Consistent error handling across all calculation types
 * - Performance optimization through caching
 * - Simplified calling code (no need to know about orchestrator, services, etc.)
 */

function calculateSafeTbr(esalStabilised: number, esalUnstabilised: number): number {
    if (!Number.isFinite(esalStabilised) || !Number.isFinite(esalUnstabilised) || esalUnstabilised <= 0) {
        console.warn(`TBR: invalid ESAL values (stabilised=${esalStabilised}, unstabilised=${esalUnstabilised}), defaulting TBR to 1.0 (no benefit)`);
        return 1.0;
    }
    return esalStabilised / esalUnstabilised;
}

export class CalculationServiceFacade {
    private static instance: CalculationServiceFacade;
    private pavedOrchestrator: PavedCalculationOrchestrator;
    private railwayOrchestrator: RailwayCalculationOrchestrator;
    private optimizedService: OptimizedExcelCellCalculatorService;
    private materialService = materialDatabaseService;
    // Cache temporarily disabled for debugging
    // private calculationCache = new Map<string, { result: ICompleteCalculationResult; timestamp: number }>();
    // private readonly CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes cache TTL

    private constructor() {
        this.pavedOrchestrator = new PavedCalculationOrchestrator();
        this.railwayOrchestrator = new RailwayCalculationOrchestrator();
        this.optimizedService = new OptimizedExcelCellCalculatorService();
        logDebug('CalculationServiceFacade', 'Service facade initialized with paved and railway orchestrators', undefined, LogTag.CALCULATIONS);
    }

    /**
     * Singleton pattern - ensures single instance across application
     */
    public static getInstance(): CalculationServiceFacade {
        if (!CalculationServiceFacade.instance) {
            CalculationServiceFacade.instance = new CalculationServiceFacade();
        }
        return CalculationServiceFacade.instance;
    }

    /**
     * Main entry point for complete paved calculations
     *
     * Orchestrates the entire calculation flow:
     * 1. Transform form data to calculation inputs
     * 2. Execute AASHTO structural analysis
     * 3. Calculate Excel-compatible cells
     * 4. Cache results for performance
     *
     * @param formData - Complete form data from UI
     * @returns Promise<ICompleteCalculationResult> - All calculation results
     */
    async executeCompleteCalculation(formData: IPavedData): Promise<ICompleteCalculationResult> {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const startTime = Date.now();

        // DEBUG: Log input data for troubleshooting
        logDebug('CalculationServiceFacade', '[DEBUG] Input formData received:', JSON.stringify(formData), LogTag.DEBUG);

        try {
            logDebug('CalculationServiceFacade', 'Starting complete calculation for form data', undefined, LogTag.CALCULATIONS);

            // Cache temporarily disabled for debugging
            // Check cache first
            // const cacheKey = this.generateCacheKey(formData);
            // const cachedResult = this.getCachedResult(cacheKey);

            // if (cachedResult) {
            //     logDebug('CalculationServiceFacade', 'Returning cached calculation result', undefined, LogTag.PERFORMANCE);
            //     return {
            //         ...cachedResult,
            //         cacheHit: true,
            //         calculationTimeMs: Date.now() - startTime,
            //     };
            // }

            // 1. Transform form data to calculation inputs using orchestrator
            const calculationInputs = await this.pavedOrchestrator.orchestrateCalculation(formData);
            logDebug('CalculationServiceFacade', 'Form data transformed to calculation inputs', undefined, LogTag.DATA_TRANSFORM);

            // 2. Use optimized service for ALL calculations (per optimization requirements)
            const optimizedResults = await this.optimizedService.calculateOptimized(calculationInputs);
            logDebug('CalculationServiceFacade', 'Optimized calculation completed', undefined, LogTag.CALCULATIONS);

            // Extract totalSN from optimized results (Step 3: Required SN)
            const totalSN = optimizedResults.step3_requiredSN.snRequired;

            // Create layerAnalysis from optimized results (Step 4: Layer Design)
            const layerAnalysis: ILayerAnalysisResult = {
                // Structural Numbers required for each layer
                sn1_required: optimizedResults.step4_layerDesign.structuralNumbers.unstabilised, // Surface SN
                sn2_required: optimizedResults.step4_layerDesign.structuralNumbers.unstabilised, // Base SN (same for now)
                sn3_required: optimizedResults.step4_layerDesign.structuralNumbers.unstabilised, // Subbase SN (same for now)
                sn1_stabilised_required: optimizedResults.step4_layerDesign.structuralNumbers.stabilised,
                sn2_stabilised_required: optimizedResults.step4_layerDesign.structuralNumbers.stabilised,
                sn3_stabilised_required: optimizedResults.step4_layerDesign.structuralNumbers.stabilised,

                layerThicknesses: {
                    surface: {
                        unstabilised: optimizedResults.step4_layerDesign.thickness.unstabilised.surface,
                        stabilised: optimizedResults.step4_layerDesign.thickness.stabilised.surface,
                    },
                    base: {
                        unstabilised: optimizedResults.step4_layerDesign.thickness.unstabilised.base,
                        stabilised: optimizedResults.step4_layerDesign.thickness.stabilised.base,
                    },
                    subbase: {
                        unstabilised: optimizedResults.step4_layerDesign.thickness.unstabilised.subbase,
                        stabilised: optimizedResults.step4_layerDesign.thickness.stabilised.subbase,
                    },
                },
            };

            // 3. Transform optimized results into the UI-expected structure (with dynamic drainage coefficients)
            const detailedCalculations = this.transformOptimizedResultsForUI(optimizedResults, calculationInputs);
            logDebug('CalculationServiceFacade', 'Transformed optimized results for UI compatibility', undefined, LogTag.DATA_TRANSFORM);

            // 4. Assemble complete result with optimized data
            const result: ICompleteCalculationResult = {
                totalSN,
                layerAnalysis,
                detailedCalculations,
                cacheHit: false,
                calculationTimeMs: optimizedResults.calculationMetadata.totalCalculationTime,
            };

            // 5. Cache the result for future use (temporarily disabled)
            // this.cacheResult(cacheKey, result);

            logDebug(
                'CalculationServiceFacade',
                'Complete calculation finished',
                {
                    totalSN,
                    calculationTimeMs: result.calculationTimeMs,
                    logW18Values: {
                        unstabilised: optimizedResults.step5_finalLogW18?.unstabilised,
                        stabilised: optimizedResults.step5_finalLogW18?.stabilised,
                    },
                    // cacheKey, // temporarily disabled
                },
                LogTag.CALCULATIONS
            );

            return result;
        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'Unknown calculation error';
            logError('CalculationServiceFacade', 'Calculation failed:', error, LogTag.ERROR_HANDLING);

            throw new Error(`Calculation failed: ${errorMsg}`);
        }
    }

    /**
     * Generate cache key for calculation inputs
     */
    private generateCacheKey(formData: IPavedData): string {
        // Create a simplified hash based on key calculation parameters
        const keyParams = {
            w18: formData.trafficLoad?.esalFinalValue,
            reliability: formData.trafficLoad?.reliabilityLevel,
            materials: {
                surface: formData.surface?.surfaceMaterial,
                base: formData.baseCourse?.baseMaterial,
                subbase: formData.subbaseCourse?.subbaseMaterial,
            },
            subgrade: {
                cbr: formData.subgrade?.cbrSubgrade,
                ev2: formData.subgrade?.ev2Subgrade,
            },
            drainage: formData.drainageDesign,
        };

        return btoa(JSON.stringify(keyParams))
            .replace(/[^a-zA-Z0-9]/g, '')
            .substring(0, 32);
    }

    /**
     * Get cached calculation result if valid (temporarily disabled)
     */
    /*
    private getCachedResult(cacheKey: string): ICompleteCalculationResult | null {
        const cached = this.calculationCache.get(cacheKey);

        if (!cached) {
            return null;
        }

        // Check if cache entry is still valid (TTL check)
        if (Date.now() - cached.timestamp > this.CACHE_TTL_MS) {
            this.calculationCache.delete(cacheKey);
            logDebug('CalculationServiceFacade', 'Cache entry expired and removed', undefined, LogTag.PERFORMANCE);
            return null;
        }

        return cached.result;
    }
    */

    /**
     * Cache calculation result (temporarily disabled)
     */
    /*
    private cacheResult(cacheKey: string, result: ICompleteCalculationResult): void {
        // Clean up old cache entries to prevent memory leaks
        this.cleanupOldCacheEntries();

        this.calculationCache.set(cacheKey, {
            result: { ...result, cacheHit: false }, // Store without cache metadata
            timestamp: Date.now(),
        });

        logDebug('CalculationServiceFacade', 'Result cached', {
            cacheKey,
            cacheSize: this.calculationCache.size,
        }, LogTag.PERFORMANCE);
    }
    */

    /**
     * Clean up expired cache entries (temporarily disabled)
     */
    /*
    private cleanupOldCacheEntries(): void {
        const now = Date.now();
        let cleanedCount = 0;

        this.calculationCache.forEach((cached, key) => {
            if (now - cached.timestamp > this.CACHE_TTL_MS) {
                this.calculationCache.delete(key);
                cleanedCount++;
            }
        });

        if (cleanedCount > 0) {
            logDebug('CalculationServiceFacade', `Cleaned up ${cleanedCount} expired cache entries`, undefined, LogTag.PERFORMANCE);
        }
    }
    */

    /**
     * Clear all cached calculations (temporarily disabled)
     * Useful for testing or when form structure changes
     */
    /*
    public clearCache(): void {
        const previousSize = this.calculationCache.size;
        this.calculationCache.clear();
        logDebug('CalculationServiceFacade', `Cache cleared. Removed ${previousSize} entries`, undefined, LogTag.PERFORMANCE);
    }
    */

    /**
     * Get cache statistics for debugging (temporarily disabled)
     */
    /*
    public getCacheStats(): { size: number; memoryUsageKB: number } {
        const size = this.calculationCache.size;
        // Rough estimate of memory usage
        const memoryUsageKB = Math.round((size * 1024) / 1024); // Very rough estimate

        return { size, memoryUsageKB };
    }
    */

    /**
     * Cache calculation result (temporarily disabled)
     */
    /*
    private cacheResult(cacheKey: string, result: ICompleteCalculationResult): void {
        // Clean up old cache entries to prevent memory leaks
        this.cleanupOldCacheEntries();

        this.calculationCache.set(cacheKey, {
            result: { ...result, cacheHit: false }, // Store without cache metadata
            timestamp: Date.now(),
        });

        logDebug('CalculationServiceFacade', 'Result cached', {
            cacheKey,
            cacheSize: this.calculationCache.size,
        }, LogTag.PERFORMANCE);
    }
    */

    /**
     * Clean up expired cache entries (temporarily disabled)
     */
    /*
    private cleanupOldCacheEntries(): void {
        const now = Date.now();
        let cleanedCount = 0;

        this.calculationCache.forEach((cached, key) => {
            if (now - cached.timestamp > this.CACHE_TTL_MS) {
                this.calculationCache.delete(key);
                cleanedCount++;
            }
        });

        if (cleanedCount > 0) {
            logDebug('CalculationServiceFacade', `Cleaned up ${cleanedCount} expired cache entries`, undefined, LogTag.PERFORMANCE);
        }
    }
    */

    /**
     * Clear all cached calculations (temporarily disabled)
     * Useful for testing or when form structure changes
     */
    /*
    public clearCache(): void {
        const previousSize = this.calculationCache.size;
        this.calculationCache.clear();
        logDebug('CalculationServiceFacade', `Cache cleared. Removed ${previousSize} entries`, undefined, LogTag.PERFORMANCE);
    }
    */

    /**
     * Get cache statistics for debugging (temporarily disabled)
     */
    /*
    public getCacheStats(): { size: number; memoryUsageKB: number } {
        const size = this.calculationCache.size;
        // Rough estimate of memory usage
        const memoryUsageKB = Math.round((size * 1024) / 1024); // Very rough estimate

        return { size, memoryUsageKB };
    }
    /**
     * Transform optimized 5-step results into the UI-expected structure
     * Creates the detailedCalculations structure that the Results_Paved component expects
     */
    private transformOptimizedResultsForUI(optimizedResults: OptimizedCalculationResults, calculationInputs?: ILayerAnalysisInputs): TransformedCalculationResults {
        const setupData = optimizedResults.step0_initialSetup;
        const validatedInputs = optimizedResults.step1_validatedInputs;
        const trafficResults = optimizedResults.step2_designTraffic;
        const snResults = optimizedResults.step3_requiredSN;
        const layerResults = optimizedResults.step4_layerDesign;
        const logW18Results = optimizedResults.step5_finalLogW18;

        // Calculate ZR from reliability (matches OptimizedExcelCellCalculatorService logic)
        const reliability = validatedInputs.reliability || 95;
        const zr = this.calculateStandardNormalDeviate(reliability);

        // CRITICAL FIX: Use dynamic drainage coefficients from calculationInputs instead of static setupData
        // This ensures UI shows the corrected coefficients calculated by PavedCalculationOrchestrator
        const dynamicDrainageCoefficients = calculationInputs?.drainageCoefficients;

        const transformedResults: TransformedCalculationResults = {
            // Basic AASHTO parameters - matches IApiResponse interface
            basicParameters: {
                dpsi: validatedInputs.deltaPSI,
                zr: zr,
                structuralNumberSN: snResults.snRequired,
                structuralNumberMeters: snResults.snRequired * UNIT_CONVERSION.INCHES_TO_METERS,
                mrSubgradeKpa: setupData.aashto.mrSubgradeKpa,
                mrSubgradePsi: setupData.aashto.mrSubgradePsi,
            },

            // Layer coefficients and properties - FIXED: Using dynamic coefficients
            layerCoefficients: {
                surface: {
                    a1: setupData.materials.surface.a1,
                    drainageCoefficient: 1.0, // Surface has no drainage coefficient
                    thickness: layerResults.thickness.unstabilised.surface,
                },
                base: {
                    a2: setupData.materials.base.a2,
                    // FIXED: Use dynamic coefficients from PavedCalculationOrchestrator
                    m2: dynamicDrainageCoefficients?.base || setupData.drainage.base.m2,
                    m2_stabilised: dynamicDrainageCoefficients?.baseStabilised || setupData.drainage.base.m2_stabilised || setupData.drainage.base.m2,
                    thickness: layerResults.thickness.unstabilised.base,
                    lcr: setupData.lcr.base, // LCR multiplier for base layer
                },
                subbase: {
                    a3: setupData.materials.subbase.a3,
                    // FIXED: Use dynamic coefficients from PavedCalculationOrchestrator (includes Excel K63 logic)
                    m3: dynamicDrainageCoefficients?.subbase || setupData.drainage.subbase.m3,
                    m3_stabilised: dynamicDrainageCoefficients?.subbaseStabilised || setupData.drainage.subbase.m3_stabilised || setupData.drainage.subbase.m3,
                    thickness: layerResults.thickness.unstabilised.subbase,
                    lcr: setupData.lcr.subbase, // LCR multiplier for subbase layer
                },
            },

            // Software design results - UI expects layer.type structure
            softwareDesign: {
                surface: {
                    unstabilised: layerResults.thickness.unstabilised.surface,
                    stabilised: layerResults.thickness.stabilised.surface,
                },
                base: {
                    unstabilised: layerResults.thickness.unstabilised.base,
                    stabilised: layerResults.thickness.stabilised.base,
                    // CENTRALIZED GEOGRID ARCHITECTURE: Populate geogridMaterialName from geogrid ID
                    geogridMaterialName: calculationInputs?.geogridIds?.base ? (this.materialService.getGeogridMaterialNameFromId(calculationInputs.geogridIds.base) ?? undefined) : undefined,
                    // CENTRALIZED GEOGRID ARCHITECTURE: Set geogrid position to standard placement above geogrid
                    geogridPosition: calculationInputs?.geogridIds?.base ? Number(GEOGRID_CONFIG.STANDARD_POSITION_ABOVE_GEOGRID) : undefined,
                },
                subbase: {
                    unstabilised: layerResults.thickness.unstabilised.subbase,
                    stabilised: layerResults.thickness.stabilised.subbase,
                    // CENTRALIZED GEOGRID ARCHITECTURE: Populate geogridMaterialName from geogrid ID
                    geogridMaterialName: calculationInputs?.geogridIds?.subbase ? (this.materialService.getGeogridMaterialNameFromId(calculationInputs.geogridIds.subbase) ?? undefined) : undefined,
                    // CENTRALIZED GEOGRID ARCHITECTURE: Set geogrid position to standard placement above geogrid
                    geogridPosition: calculationInputs?.geogridIds?.subbase ? Number(GEOGRID_CONFIG.STANDARD_POSITION_ABOVE_GEOGRID) : undefined,
                },
                totalSN: {
                    unstabilised: layerResults.structuralNumbers?.unstabilised || 0,
                    stabilised: layerResults.structuralNumbers?.stabilised || 0,
                },
            },

            // ESAL factors and traffic results
            esalFactors: {
                w18: trafficResults.w18,
                logW18Unstabilised: logW18Results.unstabilised,
                logW18Stabilised: logW18Results.stabilised,
                esalUnstabilised: logW18Results.esal.unstabilised,
                esalStabilised: logW18Results.esal.stabilised,
                tbr: calculateSafeTbr(logW18Results.esal.stabilised, logW18Results.esal.unstabilised),
                f1: snResults.aashtoFactors.f1, // AASHTO factor f1
                f2: snResults.aashtoFactors.f2, // AASHTO factor f2
                f3: snResults.aashtoFactors.f3, // AASHTO factor f3
                tgf: 1.0, // Traffic Growth Factor - default to 1.0
                vtUnstabilised: 0.0, // Default values for compatibility
                vtStabilised: 0.0,
                niUnstabilised: 0.0,
                niStabilised: 0.0,
            },

            // SN calculations - structural numbers and individual layer contributions
            snCalculations: {
                snUnstabilised: layerResults.structuralNumbers?.unstabilised || 0,
                snStabilised: layerResults.structuralNumbers?.stabilised || 0,
                // Calculate individual layer SN contributions from layer parameters - FIXED: Using dynamic coefficients
                sn1Prime: (setupData.materials.surface.a1 * (layerResults.thickness?.unstabilised?.surface || 0)) / UNIT_CONVERSION.METERS_TO_INCHES,
                sn2Prime: (setupData.materials.base.a2 * (dynamicDrainageCoefficients?.base || setupData.drainage.base.m2) * (layerResults.thickness?.unstabilised?.base || 0)) / UNIT_CONVERSION.METERS_TO_INCHES,
                sn3Prime: (setupData.materials.subbase.a3 * (dynamicDrainageCoefficients?.subbase || setupData.drainage.subbase.m3) * (layerResults.thickness?.unstabilised?.subbase || 0)) / UNIT_CONVERSION.METERS_TO_INCHES,
            },

            // Design thickness for IAASHTOResults compatibility
            designThickness: {
                unstabilised: {
                    surface: layerResults.thickness.unstabilised.surface,
                    base: layerResults.thickness.unstabilised.base,
                    subbase: layerResults.thickness.unstabilised.subbase,
                },
                stabilised: {
                    surface: layerResults.thickness.stabilised.surface,
                    base: layerResults.thickness.stabilised.base,
                    subbase: layerResults.thickness.stabilised.subbase,
                },
            },

            // Include original optimized results for debugging
            _originalOptimizedResults: optimizedResults,
        };

        // Apply decimal limitation (2 decimal places) to all numerical values in the results
        // This centralizes decimal formatting for all result displays across different project types
        return roundNumbers(transformedResults) as TransformedCalculationResults;
    }

    /**
     * Calculate Standard Normal Deviate (ZR) from reliability percentage
     * Matches the logic in MathUtilsService.calculateStandardNormalDeviate
     * Data source: ReliabilityService (centralized JSON configuration)
     */
    private calculateStandardNormalDeviate(reliability: number): number {
        return ReliabilityService.getZr(reliability);
    }

    /**
     * Railway calculation entry point
     * Provides Railway-specific calculation using dedicated orchestrator
     *
     * @param formData - Railway form data
     * @returns Promise<IRailwayOutputData> - Railway calculation results
     */
    /**
     * Railway calculation entry point with optional initial guesses for Ballast thickness
     * @param formData - Railway form data
     * @param initialGuessUnstabilized - Optional initial guess for unstabilized Ballast thickness (meters)
     * @param initialGuessStabilized - Optional initial guess for stabilized Ballast thickness (meters)
     * @returns Promise<IRailwayOutputData> - Railway calculation results
     */
    async executeRailwayCalculation(formData: IRailwayData, initialGuessUnstabilized?: number, initialGuessStabilized?: number): Promise<IRailwayOutputData> {
        const startTime = Date.now();

        try {
            logDebug('CalculationServiceFacade', 'Starting Railway calculation', undefined, LogTag.RAILWAY_CALC);

            // Forza initialGuess a 0.01 se non specificato
            const result = await this.railwayOrchestrator.orchestrateCalculation(
                formData,
                initialGuessUnstabilized !== undefined ? initialGuessUnstabilized : 0.01,
                initialGuessStabilized !== undefined ? initialGuessStabilized : 0.01
            );
            logDebug(
                'CalculationServiceFacade',
                'Railway calculation completed',
                {
                    calculationTimeMs: Date.now() - startTime,
                },
                LogTag.RAILWAY_CALC
            );

            return result;
        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'Unknown railway calculation error';
            logError('CalculationServiceFacade', 'Railway calculation failed:', error, LogTag.ERROR_HANDLING);

            throw new Error(`Railway calculation failed: ${errorMsg}`);
        }
    }
}

/**
 * Singleton instance for global access
 * Maintains consistent state across the application
 */
export const calculationServiceFacade = CalculationServiceFacade.getInstance();
